import React, { useState, useEffect } from 'react';
import { Package, RefreshCw } from 'lucide-react';
import authService from '../services/AuthService';
import { API_BASE } from '../consts';
import DealsViewer from '../components/DealsViewer';

const A2ADealsPage = ({ user }) => {
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checkingGated, setCheckingGated] = useState(false);

    useEffect(() => {
        loadDeals();
    }, []);

    const loadDeals = async () => {
        setLoading(true);
        try {
            const response = await authService.authenticatedFetch(API_BASE + '/api/a2a/deals');
            const data = await response.json();

            if (response.ok && data.deals) {
                setDeals(data.deals);

                // Auto-check gated status after loading deals
                checkGatedStatusBulk(data.deals);
            }
        } catch (error) {
            console.error('Failed to load A2A deals:', error);
        } finally {
            setLoading(false);
        }
    };

    const checkGatedStatusBulk = async (dealsToCheck) => {
        if (!dealsToCheck || dealsToCheck.length === 0) return;

        setCheckingGated(true);
        try {
            // Extract unique ASINs
            const asins = [...new Set(
                dealsToCheck
                    .map(deal => deal.marketplace_data?.asin || deal.product_id)
                    .filter(Boolean)
            )];

            if (asins.length === 0) {
                setCheckingGated(false);
                return;
            }

            // Check in batches of 10
            const batchSize = 10;

            for (let i = 0; i < asins.length; i += batchSize) {
                if (window.location.pathname !== '/a2a-deals') break;
                const batch = asins.slice(i, i + batchSize);

                try {
                    const response = await authService.authenticatedFetch(
                        API_BASE + '/api/a2a/check-gated-bulk',
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ asins: batch })
                        }
                    );

                    const data = await response.json();

                    if (response.ok && data.results) {
                        // Update deals immediately after each batch
                        setDeals(prevDeals =>
                            prevDeals.map(deal => {
                                const asin = deal.marketplace_data?.asin || deal.product_id;
                                const liveGatedStatus = data.results[asin];

                                if (liveGatedStatus) {
                                    return {
                                        ...deal,
                                        gated_status: liveGatedStatus
                                    };
                                }
                                return deal;
                            })
                        );
                    }
                } catch (batchError) {
                    console.error(`Gated check failed for batch ${i}, skipping:`, batchError);
                }
            }

        } catch (error) {
            console.error('Failed to check gated status:', error);
        } finally {
            setCheckingGated(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
            {/* Animated background */}
            <div className="fixed inset-0 opacity-10 pointer-events-none">
                <div className="absolute top-0 -left-4 w-96 h-96 rounded-full filter blur-3xl"
                    style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
                <div className="absolute bottom-0 -right-4 w-96 h-96 rounded-full filter blur-3xl"
                    style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                        <Package className="text-teal-600" size={40} />
                        Amazon to Amazon Flips
                    </h1>
                    <p className="text-gray-600">
                        Find profitable A2A Flips - buy low on Amazon now, sell high on Amazon later
                    </p>

                    {checkingGated && (
                        <div className="mt-4 flex items-center gap-2 text-blue-600">
                            <RefreshCw className="animate-spin" size={16} />
                            <span className="text-sm">Checking gated status for your account...</span>
                        </div>
                    )}
                </div>

                {/* Deals Viewer - REMOVED key prop */}
                <DealsViewer
                    deals={deals}
                    loading={loading}
                    title=""
                    showFilters={true}
                    user={user}
                    defaultAutoPackDetection={false}
                />
            </div>
        </div>
    );
};

export default A2ADealsPage;